<template>
    <v-dialog v-model="show" max-width="800px" :persistent="loading" scrollable>
        <v-card class="pa-4">
            <v-overlay :value="loading || uploading" absolute z-index="10" color="white" opacity="0.8">
                <div class="d-flex flex-column align-center text-center">
                    <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
                    <h3 class="mt-4 primary--text">{{ uploading ? 'Enviando arquivo' : 'Salvando prontuário' }}</h3>
                    <p class="primary--text text--lighten-1">Isso pode levar alguns instantes...</p>
                </div>
            </v-overlay>
            <v-card-title>
                Prontuário do Atendimento
            </v-card-title>
            <v-card-text>
                <v-form ref="formRecord">
                    <v-row>
                        <v-col v-if="error_message" cols="12">
                            <v-alert type="error darken-4">
                                {{ error_message }}
                            </v-alert>
                        </v-col>
                        <v-col v-if="success_message" cols="12">
                            <v-alert type="success">
                                {{ success_message }}
                            </v-alert>
                        </v-col>
                        
                        <v-col cols="12">
                            <p class="text-subtitle-1">Conteúdo do Prontuário</p>
                            <vue-editor id="tour-medical-record-editor" v-model="content" placeholder="Descreva os detalhes do atendimento aqui..."></vue-editor>
                        </v-col>

                        <v-col cols="12" id="tour-medical-record-attachments">
                            <v-divider class="my-4"></v-divider>
                            <p class="text-subtitle-1 mb-2">Anexos</p>
                            <v-alert type="info" outlined dense class="mb-4 text-caption">
                                Limite de 50MB por arquivo. Máximo de 10 arquivos por prontuário.
                            </v-alert>
                            
                            <!-- Lista de Anexos -->
                            <v-list v-if="attachments.length > 0" class="mb-4">
                                <v-list-item v-for="attachment in attachments" :key="attachment.id" class="px-0">
                                    <v-list-item-avatar>
                                        <v-icon :color="getIconColor(attachment.file_type)">
                                            {{ getIcon(attachment.file_type) }}
                                        </v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ attachment.file_name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ formatSize(attachment.file_size) }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action class="d-flex flex-row">
                                        <v-btn icon color="primary" @click="viewAttachment(attachment)" title="Visualizar">
                                            <v-icon>mdi-eye</v-icon>
                                        </v-btn>
                                        <v-btn icon color="secondary" @click="downloadAttachment(attachment)" title="Baixar">
                                            <v-icon>mdi-download</v-icon>
                                        </v-btn>
                                        <v-btn icon color="error" @click="deleteAttachment(attachment)" title="Remover" :disabled="loading" :loading="deleting === attachment.id">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>

                            <p v-else class="text-body-2 text-center my-4 grey--text">Nenhum arquivo anexado.</p>

                            <!-- Area de Upload -->
                            <div v-if="attachments.length < 10" class="mt-2">
                                <v-btn 
                                    color="primary" 
                                    outlined 
                                    @click="$refs.fileInput.click()" 
                                    :loading="uploading || loading"
                                    :disabled="uploading || loading"
                                >
                                    <v-icon left>mdi-paperclip</v-icon> Adicionar Anexo
                                </v-btn>
                                <input 
                                    type="file" 
                                    ref="fileInput" 
                                    style="display: none" 
                                    accept="image/*,application/pdf,video/*,audio/*" 
                                    @change="handleFileChange"
                                >
                                <p class="text-caption grey--text mt-1">Selecione um arquivo (Imagem, PDF, Vídeo ou Áudio)</p>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-row no-gutters justify="space-between">
                    <v-col cols="auto">
                        <v-btn :disabled="loading || uploading" color="secondary" outlined elevation="0" @click="close">
                            <v-icon left>mdi-close</v-icon> Fechar
                        </v-btn>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn :disabled="loading || uploading" :loading="loading" color="primary" dark elevation="0" @click="save">
                            Salvar <v-icon right>mdi-check-bold</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>

        <!-- Dialog de Visualização -->
        <v-dialog v-model="showViewer" max-width="90%" fullscreen v-if="currentAttachment">
            <v-card>
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click="showViewer = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ currentAttachment.file_name }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn dark text @click="downloadAttachment(currentAttachment)">
                            Download
                        </v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-card-text class="pa-0 d-flex justify-center align-center" style="height: calc(100vh - 64px); background-color: #eee;">
                    <v-progress-circular v-if="loadingView" indeterminate color="primary"></v-progress-circular>
                    <div v-else class="d-flex justify-center align-center" style="width: 100%; height: 100%;">
                        <img v-if="currentAttachment.file_type.startsWith('image/')" :src="viewUrl" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
                        <iframe v-else-if="currentAttachment.file_type === 'application/pdf'" :src="viewUrl" width="100%" height="100%" style="border: none;"></iframe>
                        <video v-else-if="currentAttachment.file_type.startsWith('video/')" :src="viewUrl" controls style="max-width: 100%; max-height: 100%;"></video>
                        <audio v-else-if="currentAttachment.file_type.startsWith('audio/')" :src="viewUrl" controls></audio>
                        <div v-else class="text-center">
                            <v-icon size="64" color="grey">mdi-file</v-icon>
                            <p class="mt-4">Visualização não disponível para este tipo de arquivo.</p>
                            <v-btn color="primary" @click="downloadAttachment(currentAttachment)">Fazer Download</v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { VueEditor } from 'vue2-editor';
import axios from 'axios';

export default {
    components: {
        VueEditor
    },
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false
        },
        sessionData: {
            type: Object,
            required: false,
            default: null
        }
    },
    data() {
        return {
            show: this.value,
            loading: false,
            uploading: false,
            deleting: null,
            error_message: null,
            success_message: null,
            recordId: null,
            content: '',
            attachments: [],
            fileToUpload: null,
            
            // Viewer
            showViewer: false,
            currentAttachment: null,
            viewUrl: null,
            loadingView: false
        };
    },
    watch: {
        value() {
            this.show = this.value;
            if (this.show && this.sessionData) {
                this.loadRecord();
            }
        },
        show(val) {
            if (!val) {
                this.$emit("input", false);
                this.resetForm();
            }
        },
        showViewer(val) {
            if (!val && this.viewUrl) {
                URL.revokeObjectURL(this.viewUrl);
                this.viewUrl = null;
                this.currentAttachment = null;
            }
        }
    },
    mounted() {
        window.$dialogMedicalRecord = this;
    },
    beforeDestroy() {
        if (window.$dialogMedicalRecord === this) {
            window.$dialogMedicalRecord = null;
        }
    },
    methods: {
        ...mapActions("sessions", [
            "ActionDoSaveMedicalRecord",
            "ActionDoGetMedicalRecord",
            "ActionDoUploadAttachment",
            "ActionDoDeleteAttachment"
        ]),
        resetForm() {
            this.recordId = null;
            this.content = '';
            this.attachments = [];
            this.fileToUpload = null;
            this.error_message = null;
            this.success_message = null;
        },
        loadRecord() {
            this.loading = true;
            this.ActionDoGetMedicalRecord(this.sessionData.id)
                .then(res => {
                    if (res.data) {
                        this.recordId = res.data.id;
                        this.content = res.data.content || '';
                        this.attachments = res.data.attachments || [];
                    } else {
                        this.recordId = null;
                        this.content = '';
                        this.attachments = [];
                    }
                })
                .catch(err => {
                    this.error_message = err.response?.data?.error || "Erro ao carregar prontuário";
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        save() {
            if (!this.content) {
                this.error_message = "O conteúdo do prontuário não pode ficar vazio.";
                return;
            }

            this.loading = true;
            this.error_message = null;
            this.success_message = null;

            this.ActionDoSaveMedicalRecord({
                session_id: this.sessionData.id,
                content: this.content
            })
                .then(res => {
                    this.success_message = "Prontuário salvo com sucesso!";
                    if (!this.recordId && res.data && res.data.id) {
                        this.recordId = res.data.id;
                    }
                    this.$emit('saved');
                })
                .catch(err => {
                    this.error_message = err.response?.data?.error || "Erro ao salvar prontuário";
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async handleFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!this.recordId) {
                try {
                    this.loading = true;
                    const res = await this.ActionDoSaveMedicalRecord({
                        session_id: this.sessionData.id,
                        content: this.content || '<p></p>'
                    });
                    if (res.data && res.data.id) {
                        this.recordId = res.data.id;
                    }
                } catch(err) {
                    this.error_message = err.response?.data?.error || "Erro ao salvar prontuário antes do upload";
                    this.loading = false;
                    this.$refs.fileInput.value = '';
                    return;
                }
                this.loading = false;
            }
            
            this.fileToUpload = file;
            this.uploadFile();
            this.$refs.fileInput.value = '';
        },
        uploadFile() {
            if (!this.fileToUpload) return;
            
            if (this.fileToUpload.size > 50 * 1024 * 1024) {
                this.error_message = "O arquivo excede o limite de 50MB.";
                this.fileToUpload = null;
                return;
            }

            this.uploading = true;
            this.error_message = null;

            let formData = new FormData();
            formData.append('file', this.fileToUpload);

            this.ActionDoUploadAttachment({
                record_id: this.recordId,
                formData: formData
            })
                .then(res => {
                    if (res.data) {
                        this.attachments.push(res.data);
                        this.success_message = "Arquivo anexado com sucesso!";
                    }
                })
                .catch(err => {
                    this.error_message = err.response?.data?.error || "Erro ao anexar arquivo";
                })
                .finally(() => {
                    this.uploading = false;
                    this.fileToUpload = null;
                });
        },
        deleteAttachment(attachment) {
            if (!confirm(`Deseja realmente remover o anexo ${attachment.file_name}?`)) return;

            this.deleting = attachment.id;
            this.error_message = null;

            this.ActionDoDeleteAttachment(attachment.id)
                .then(() => {
                    this.attachments = this.attachments.filter(a => a.id !== attachment.id);
                    this.success_message = "Anexo removido!";
                })
                .catch(err => {
                    this.error_message = err.response?.data?.error || "Erro ao remover anexo";
                })
                .finally(() => {
                    this.deleting = null;
                });
        },
        viewAttachment(attachment) {
            this.currentAttachment = attachment;
            this.showViewer = true;
            this.loadingView = true;

            const url = `${process.env.VUE_APP_API_URL}medical-records/attachments/${attachment.id}/view`;
            const token = this.$store.state.auth.token;

            axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob'
            })
            .then(res => {
                this.viewUrl = URL.createObjectURL(res.data);
            })
            .catch(err => {
                console.error(err);
                this.showViewer = false;
                this.error_message = "Erro ao visualizar arquivo";
            })
            .finally(() => {
                this.loadingView = false;
            });
        },
        downloadAttachment(attachment) {
            const url = `${process.env.VUE_APP_API_URL}medical-records/attachments/${attachment.id}/download`;
            const token = this.$store.state.auth.token;

            axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob'
            })
            .then(res => {
                const downloadUrl = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = downloadUrl;
                a.download = attachment.file_name;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(downloadUrl);
                a.remove();
            })
            .catch(err => {
                console.error(err);
                this.error_message = "Erro ao baixar arquivo";
            });
        },
        formatSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        getIcon(mimeType) {
            if (mimeType.startsWith('image/')) return 'mdi-image';
            if (mimeType === 'application/pdf') return 'mdi-file-pdf-box';
            if (mimeType.startsWith('video/')) return 'mdi-video';
            if (mimeType.startsWith('audio/')) return 'mdi-file-music';
            return 'mdi-file';
        },
        getIconColor(mimeType) {
            if (mimeType.startsWith('image/')) return 'blue';
            if (mimeType === 'application/pdf') return 'red';
            if (mimeType.startsWith('video/')) return 'purple';
            if (mimeType.startsWith('audio/')) return 'green';
            return 'grey';
        },
        close() {
            this.show = false;
        }
    }
};
</script>
